import apiClient, { type ApiError } from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  ProductListResponse,
  ProductListQuery,
  ProductListItem,
  ProductFormPayload,
  RiskMatrixApiResponse,
  RiskLevelApiResponse,
  RiskLabel,
  CommTemplatesResponse,
  EmailTemplateListItem,
} from '../types'

function mapListParams(q: ProductListQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    perPage: q.perPage,
    page: q.page,
    orderBy: q.orderBy,
    sortBy: q.sortBy,
  }
  if (q.search) params.search = q.search
  if (q.is_active !== undefined && q.is_active !== '') {
    params.is_active = q.is_active
  }
  return params
}

export async function fetchProducts(query: ProductListQuery): Promise<ProductListResponse> {
  try {
    const response = await apiClient.get<ProductListResponse>(API_ENDPOINTS.PRODUCT_LIST, {
      params: mapListParams(query),
    })
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Error loading products. Please try again.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function fetchProductDetail(productId: string | number): Promise<ProductListItem & Record<string, unknown>> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.PRODUCT_DETAIL(productId))
    return response.data as ProductListItem & Record<string, unknown>
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load product details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function createProduct(payload: ProductFormPayload): Promise<{ product_id?: number }> {
  try {
    const response = await apiClient.post<{ product_id?: number }>(API_ENDPOINTS.PRODUCT_LIST, payload)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to create product'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function updateProduct(productId: string | number, payload: ProductFormPayload): Promise<unknown> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PRODUCT_UPDATE(productId), payload)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update product'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function deleteProduct(productId: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.PRODUCT_DETAIL(productId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to delete product'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular GET `/api/product/can_delete_product/:id` — body may be plain string message or wrapped. */
export async function fetchCanDeleteProductMessage(productId: number): Promise<string | null> {
  try {
    const response = await apiClient.get<string | { message?: string }>(
      API_ENDPOINTS.PRODUCT_CAN_DELETE(productId),
    )
    const d = response.data
    if (typeof d === 'string') return d || null
    if (d && typeof d === 'object' && typeof d.message === 'string') return d.message
    return d ? String(d) : null
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Error checking product deletion'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function fetchRiskMatrix(productId: string | number): Promise<RiskMatrixApiResponse> {
  try {
    const response = await apiClient.get<RiskMatrixApiResponse>(API_ENDPOINTS.PRODUCT_RISK_MATRIX(productId))
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load risk matrix'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function saveRiskMatrix(productId: string | number, payload: Record<string, unknown>): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.PRODUCT_RISK_MATRIX(productId), payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to save risk matrix'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular CS2NEW-1463 — GET `/api/product/:id/risk_level`. */
export async function fetchRiskLevel(productId: string | number): Promise<RiskLevelApiResponse> {
  try {
    const response = await apiClient.get<RiskLevelApiResponse>(API_ENDPOINTS.PRODUCT_RISK_LEVEL(productId))
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load risk level'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular CS2NEW-1463 — POST `/api/product/:id/risk_level` with `{ risk_labels }`. */
export async function saveRiskLevel(
  productId: string | number,
  payload: { risk_labels: RiskLabel[] },
): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.PRODUCT_RISK_LEVEL(productId), payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to save risk level'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function fetchCommunicationWorkflows(
  productId: string | number,
): Promise<CommTemplatesResponse> {
  try {
    const response = await apiClient.get<CommTemplatesResponse>(
      API_ENDPOINTS.PRODUCT_COMM_TEMPLATES(productId),
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load communication template workflows'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function fetchWorkflowEmailTemplates(
  workflowId: number,
  productId: string | number,
): Promise<EmailTemplateListItem[]> {
  try {
    const response = await apiClient.get<EmailTemplateListItem[]>(
      API_ENDPOINTS.WORKFLOW_EMAIL_TEMPLATES(workflowId),
      { params: { product_id: productId } },
    )
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load templates for workflow'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function saveCommunicationTemplates(
  productId: string | number,
  body: { workflow_id: number; template_ids: number[] },
): Promise<{ mapped_template_ids?: number[]; mapped_count?: number; last_saved_at?: string; last_saved_by?: string }> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PRODUCT_COMM_TEMPLATES(productId), body)
    return (response.data || {}) as {
      mapped_template_ids?: number[]
      mapped_count?: number
      last_saved_at?: string
      last_saved_by?: string
    }
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to save communication templates'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
